import type { Flow, FlowNode, FlowAction } from "@learnengine/shared-types";
import { templateSupportsAction } from "../templates/registry";

export interface CommandExecutorEvents {
  onNodeStart?: (node: FlowNode) => void;
  onNodeComplete?: (node: FlowNode) => void;
  onAction?: (action: FlowAction, node: FlowNode, localTimeSeconds: number) => void;
}

/**
 * CommandExecutor is timeline-agnostic: you call update(currentTimeSeconds)
 * and it figures out which node is active and which actions should fire.
 * It NEVER draws; it only dispatches events.
 */
export class CommandExecutor {
  private flow: Flow | null = null;
  private activeNode: FlowNode | null = null;
  private activeNodeStartTime = 0;
  private firedActionIds = new Set<string>();

  constructor(private readonly events: CommandExecutorEvents = {}) {}

  loadFlow(flow: Flow) {
    this.flow = flow;
    this.activeNode = null;
    this.activeNodeStartTime = 0;
    this.firedActionIds.clear();
  }

  reset() {
    this.activeNode = null;
    this.activeNodeStartTime = 0;
    this.firedActionIds.clear();
  }

  /**
   * Update should be called with the global timeline time (seconds).
   */
  update(currentTimeSeconds: number) {
    if (!this.flow) return;

    let accumulated = 0;
    let current: FlowNode | null = null;

    for (const node of this.flow.nodes) {
      const nextAccumulated = accumulated + node.durationSeconds;
      if (currentTimeSeconds >= accumulated && currentTimeSeconds < nextAccumulated) {
        current = node;
        this.ensureNode(activeNode => {
          if (!activeNode || activeNode.id !== node.id) {
            if (activeNode) this.events.onNodeComplete?.(activeNode);
            this.activeNode = node;
            this.activeNodeStartTime = accumulated;
            this.firedActionIds.clear();
            this.events.onNodeStart?.(node);
          }
        });
        break;
      }
      accumulated = nextAccumulated;
    }

    if (!current && this.activeNode) {
      // Timeline moved past the last node
      this.events.onNodeComplete?.(this.activeNode);
      this.activeNode = null;
      return;
    }

    if (current) {
      const localTime = currentTimeSeconds - this.activeNodeStartTime;
      // Simple scheduling: if an action has `at` seconds, fire when localTime >= at; otherwise fire at node start.
      for (let index = 0; index < current.actions.length; index++) {
        const action = current.actions[index];
        const key = `${current.id}:${index}`;
        if (this.firedActionIds.has(key)) continue;

        const at = typeof (action as any).at === "number" ? (action as any).at : 0;
        if (localTime >= at) {
          this.firedActionIds.add(key);
          // Validate action support before firing.
          if (!templateSupportsAction(current.template, action.type)) {
            // eslint-disable-next-line no-console
            console.warn(
              `CommandExecutor: Template "${current.template}" does not support action "${action.type}"; skipping.`,
              { nodeId: current.id, action },
            );
            // Skip unsupported actions gracefully.
            continue;
          }
          try {
            this.events.onAction?.(action, current, localTime);
          } catch (err) {
            // Fail gracefully; log for now.
            // eslint-disable-next-line no-console
            console.warn("CommandExecutor action error", { action, err });
          }
        }
      }
    }
  }

  private ensureNode(fn: (activeNode: FlowNode | null) => void) {
    fn(this.activeNode);
  }
}
